const CLOUD_SITE_URL = "https://ikai.com.tr";
const CLOUD_DEV_SITE_URL = "https://dev.ikai.com.tr";
const SCHEMA_VERSION = "runtime-public-config.v1";
const GA_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{6,20}$/;

export type RuntimeProfile = "cloud" | "onprem";

export interface RuntimePublicConfig {
  schemaVersion: typeof SCHEMA_VERSION;
  profile: RuntimeProfile;
  siteUrl: string;
  apiOriginUrl: string;
  apiV1Url: string;
  sameOriginApi: boolean;
  features: {
    analytics: boolean;
    devQuickLogin: boolean;
    legacyErrorLogger: boolean;
    kpiOkrAnalyticsBundle: boolean;
  };
  integrations: {
    turnstileSiteKey: string;
    turnstileBypass: boolean;
    gaMeasurementId: string;
    googleMapsApiKey: string;
    googleSiteVerification: string;
  };
  clientTuning: {
    authNoisePollingPauseMs: number;
    authNoisePollingFailThreshold: number;
  };
}

function envValue(key: string): string {
  if (typeof process === "undefined") return "";
  return String(process.env[key] || "");
}

function boolValue(value: string, fallback: boolean): boolean {
  if (value === "true") return true;
  if (value === "false") return false;
  return fallback;
}

function numberValue(value: string, fallback: number, min: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < min) return fallback;
  return Math.floor(parsed);
}

function normalizeUrl(value: string, field: string): string {
  try {
    return new URL(value).toString().replace(/\/$/, "");
  } catch {
    throw new Error(`${field}_INVALID`);
  }
}

function stripApiPath(value: string): string {
  const url = normalizeUrl(value, "API_URL");
  return url.replace(/\/api\/v1$/, "").replace(/\/api$/, "");
}

function apiV1Url(origin: string): string {
  return `${origin.replace(/\/$/, "")}/api/v1`;
}

function normalizeAnalyticsConfig(
  enabled: boolean | undefined,
  measurementId: string | undefined
): { enabled: boolean; measurementId: string } {
  const normalizedMeasurementId = String(measurementId || "").trim();
  if (
    enabled !== true ||
    !GA_MEASUREMENT_ID_PATTERN.test(normalizedMeasurementId)
  ) {
    return { enabled: false, measurementId: "" };
  }
  return { enabled: true, measurementId: normalizedMeasurementId };
}

function isCloudHost(hostname: string): boolean {
  return (
    hostname === "ikai.com.tr" ||
    hostname === "www.ikai.com.tr" ||
    hostname === "dev.ikai.com.tr" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1"
  );
}

function getBrowserFallbackConfig(): RuntimePublicConfig | null {
  if (typeof window === "undefined") return null;
  const origin = window.location.origin.replace(/\/$/, "");
  const hostname = window.location.hostname.toLowerCase();
  const profile: RuntimeProfile = isCloudHost(hostname) ? "cloud" : "onprem";
  return buildConfig({
    profile,
    siteUrl: origin,
    apiOriginUrl: origin,
    analytics: profile === "cloud",
  });
}

function getBrowserOnpremOriginOverride(): string | null {
  if (typeof window === "undefined") return null;
  const hostname = window.location.hostname.toLowerCase();
  if (isCloudHost(hostname)) return null;
  return window.location.origin.replace(/\/$/, "");
}

function getInlineConfig(): RuntimePublicConfig | null {
  if (typeof window === "undefined") return null;
  const config = (
    window as Window & {
      __IKAI_RUNTIME_CONFIG__?: RuntimePublicConfig;
    }
  ).__IKAI_RUNTIME_CONFIG__;
  if (!config || config.schemaVersion !== SCHEMA_VERSION) return null;
  return validateRuntimePublicConfig(config);
}

function buildConfig(input: {
  profile: RuntimeProfile;
  siteUrl: string;
  apiOriginUrl: string;
  analytics?: boolean;
  devQuickLogin?: boolean;
  legacyErrorLogger?: boolean;
  kpiOkrAnalyticsBundle?: boolean;
  turnstileSiteKey?: string;
  turnstileBypass?: boolean;
  gaMeasurementId?: string;
  googleMapsApiKey?: string;
  googleSiteVerification?: string;
  authNoisePollingPauseMs?: number;
  authNoisePollingFailThreshold?: number;
}): RuntimePublicConfig {
  const siteUrl = normalizeUrl(input.siteUrl, "RUNTIME_SITE_URL");
  const apiOriginUrl = stripApiPath(input.apiOriginUrl || siteUrl);
  const analytics = normalizeAnalyticsConfig(
    input.analytics,
    input.gaMeasurementId
  );
  const isProduction = envValue("NODE_ENV") === "production";
  const turnstileBypass =
    input.turnstileBypass === true &&
    !isProduction &&
    input.profile !== "onprem";

  return validateRuntimePublicConfig({
    schemaVersion: SCHEMA_VERSION,
    profile: input.profile,
    siteUrl,
    apiOriginUrl,
    apiV1Url: apiV1Url(apiOriginUrl),
    sameOriginApi: apiOriginUrl === siteUrl,
    features: {
      analytics: analytics.enabled,
      devQuickLogin: input.devQuickLogin === true && !isProduction,
      legacyErrorLogger: input.legacyErrorLogger === true,
      kpiOkrAnalyticsBundle: input.kpiOkrAnalyticsBundle === true,
    },
    integrations: {
      turnstileSiteKey: input.turnstileSiteKey || "",
      turnstileBypass,
      gaMeasurementId: analytics.measurementId,
      googleMapsApiKey: input.googleMapsApiKey || "",
      googleSiteVerification: input.googleSiteVerification || "",
    },
    clientTuning: {
      authNoisePollingPauseMs: input.authNoisePollingPauseMs || 300000,
      authNoisePollingFailThreshold: input.authNoisePollingFailThreshold || 3,
    },
  });
}

function validateRuntimePublicConfig(
  config: RuntimePublicConfig
): RuntimePublicConfig {
  if (config.schemaVersion !== SCHEMA_VERSION) {
    throw new Error("RUNTIME_PUBLIC_CONFIG_SCHEMA_INVALID");
  }
  if (config.profile !== "cloud" && config.profile !== "onprem") {
    throw new Error("RUNTIME_PUBLIC_CONFIG_PROFILE_INVALID");
  }
  const siteUrl = normalizeUrl(config.siteUrl, "RUNTIME_SITE_URL");
  const apiOriginUrl = stripApiPath(config.apiOriginUrl);
  const analytics = normalizeAnalyticsConfig(
    config.features?.analytics,
    config.integrations?.gaMeasurementId
  );
  if (config.profile === "onprem") {
    if (!siteUrl || siteUrl === CLOUD_SITE_URL) {
      throw new Error("ONPREM_SITE_URL_REQUIRED");
    }
    if (!apiOriginUrl || apiOriginUrl === CLOUD_SITE_URL) {
      throw new Error("ONPREM_API_URL_REQUIRED");
    }
  }
  return {
    ...config,
    siteUrl,
    apiOriginUrl,
    apiV1Url: apiV1Url(apiOriginUrl),
    sameOriginApi: apiOriginUrl === siteUrl,
    features: {
      ...config.features,
      analytics: analytics.enabled,
    },
    integrations: {
      ...config.integrations,
      gaMeasurementId: analytics.measurementId,
    },
  };
}

export function getServerRuntimePublicConfig(): RuntimePublicConfig {
  const profile: RuntimeProfile =
    envValue("IKAI_RUNTIME_PROFILE") === "onprem" ||
    envValue("NEXT_PUBLIC_RUNTIME_PROFILE") === "onprem"
      ? "onprem"
      : "cloud";
  const cloudDefault =
    envValue("IKAI_ENV") === "dev" ? CLOUD_DEV_SITE_URL : CLOUD_SITE_URL;
  const siteUrl =
    envValue("APP_BASE_URL") ||
    envValue("FRONTEND_URL") ||
    envValue("NEXT_PUBLIC_SITE_URL") ||
    cloudDefault;
  const apiOriginUrl =
    envValue("API_BASE_URL") || envValue("NEXT_PUBLIC_API_URL") || siteUrl;

  return buildConfig({
    profile,
    siteUrl,
    apiOriginUrl,
    analytics: boolValue(
      envValue("IKAI_RUNTIME_ENABLE_ANALYTICS") ||
        envValue("NEXT_PUBLIC_ENABLE_ANALYTICS"),
      false
    ),
    devQuickLogin: boolValue(
      envValue("NEXT_PUBLIC_ENABLE_DEV_QUICK_LOGIN"),
      false
    ),
    legacyErrorLogger: boolValue(
      envValue("NEXT_PUBLIC_ENABLE_LEGACY_ERROR_LOGGER"),
      false
    ),
    kpiOkrAnalyticsBundle:
      envValue("NEXT_PUBLIC_KPI_OKR_ANALYTICS_BUNDLE") === "1",
    turnstileSiteKey: envValue("NEXT_PUBLIC_TURNSTILE_SITE_KEY"),
    turnstileBypass: boolValue(envValue("NEXT_PUBLIC_TURNSTILE_BYPASS"), false),
    gaMeasurementId:
      envValue("IKAI_RUNTIME_GA_MEASUREMENT_ID") ||
      envValue("NEXT_PUBLIC_GA_MEASUREMENT_ID"),
    googleMapsApiKey: envValue("NEXT_PUBLIC_GOOGLE_MAPS_API_KEY"),
    googleSiteVerification: envValue("NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION"),
    authNoisePollingPauseMs: numberValue(
      envValue("NEXT_PUBLIC_AUTH_NOISE_POLLING_PAUSE_MS"),
      300000,
      1000
    ),
    authNoisePollingFailThreshold: numberValue(
      envValue("NEXT_PUBLIC_AUTH_NOISE_POLLING_FAIL_THRESHOLD"),
      3,
      1
    ),
  });
}

export function getPublicRuntimeConfig(): RuntimePublicConfig {
  if (envValue("RUNTIME_PUBLIC_CONFIG_ENABLED") === "false") {
    return getServerRuntimePublicConfig();
  }
  const profileEnv =
    envValue("IKAI_RUNTIME_PROFILE") || envValue("NEXT_PUBLIC_RUNTIME_PROFILE");
  if (profileEnv === "onprem") {
    return getInlineConfig() || getServerRuntimePublicConfig();
  }
  return (
    getInlineConfig() ||
    getBrowserFallbackConfig() ||
    getServerRuntimePublicConfig()
  );
}

export function getPublicSiteUrl(): string {
  return getPublicRuntimeConfig().siteUrl;
}

export function getPublicApiUrl(): string {
  const onpremOrigin = getBrowserOnpremOriginOverride();
  if (onpremOrigin) return apiV1Url(onpremOrigin);
  return getPublicRuntimeConfig().apiV1Url;
}

export function getPublicApiOriginUrl(): string {
  const onpremOrigin = getBrowserOnpremOriginOverride();
  if (onpremOrigin) return onpremOrigin;
  return getPublicRuntimeConfig().apiOriginUrl;
}

export function isOnpremProfile(): boolean {
  return getPublicRuntimeConfig().profile === "onprem";
}

export function serializeRuntimePublicConfig(
  config: RuntimePublicConfig
): string {
  return JSON.stringify(validateRuntimePublicConfig(config)).replace(
    /</g,
    "\\u003c"
  );
}
